import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { adminRoleOptions, unknownOption } from '@constants/admins.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatFullName } from '@formatters/fullName.formatter';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import {
  updateAdminSchema,
  type UpdateAdminDto,
  type UpdateAdminFormInput,
  type UpdateAdminPayload,
} from '@services/admins/dtos/update.dto';
import { adminService } from '@services/admins/service';
import type { GetAllAdminsToSelectOutput } from '@services/admins/types/getAllToSelect.type';
import type { GetAdminByIdOutput } from '@services/admins/types/getById.type';
import type { UpdateAdminOutput } from '@services/admins/types/update.type';

import { ArchiveAdminAlert } from './@components/ArchiveAlert';
import { RestoreAdminAlert } from './@components/RestoreAlert';

function adminQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.admins.detail(id),
    queryFn: () => adminService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute('/_layout/(private)/admins/$id/update')({
  component: AdminUpdate,
  loader: async ({ context, params }) => {
    const options = adminQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
});

function AdminUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id } = Route.useParams();

  const navigate = Route.useNavigate();

  const {
    data: { admin },
  } = useSuspenseQuery(adminQueryOptions(id));

  const form = useForm<UpdateAdminFormInput, unknown, UpdateAdminDto>({
    resolver: zodResolver(updateAdminSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: admin.firstName ?? '',
      lastName: admin.lastName,
      phone: admin.phone ?? '',
      role:
        adminRoleOptions.find(role => role.value === admin.role)?.value ??
        unknownOption.value,
    },
  });

  const { isPending, mutate: updateAdmin } = useMutation<
    UpdateAdminOutput,
    unknown,
    UpdateAdminPayload
  >({
    mutationFn: adminService.update,
    onError: handleRequestError,
    onSuccess: async ({ admin: updatedAdmin }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: queryKeys.admins.all }),
        queryClient.cancelQueries({ queryKey: queryKeys.admins.detail(id) }),
        queryClient.cancelQueries({ queryKey: queryKeys.admins.select }),
      ]);

      queryClient.setQueryData<GetAdminByIdOutput>(
        queryKeys.admins.detail(id),
        { admin: updatedAdmin },
      );

      queryClient.setQueryData<GetAllAdminsToSelectOutput>(
        queryKeys.admins.select,
        oldData => {
          if (!oldData) {
            return oldData;
          }

          return {
            list: oldData.list.map(option =>
              option.value === updatedAdmin.id
                ? {
                    ...option,
                    label: formatFullName({
                      firstName: updatedAdmin.firstName,
                      lastName: updatedAdmin.lastName,
                    }),
                  }
                : option,
            ),
          };
        },
      );

      await queryClient.invalidateQueries({
        queryKey: queryKeys.admins.all,
        refetchType: 'none',
      });

      navigate({
        replace: true,
        to: '/admins',
      });
      toast.success('Success', {
        description: 'Admin updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateAdmin({
      body: {
        ...values,
        phone: values.phone ?? '',
      },
      id,
    });
  });

  const adminName = formatFullName({
    firstName: admin.firstName,
    lastName: admin.lastName,
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Update admin" to="/admins" />

      <form className="mx-auto mt-5 space-y-2 px-4" onSubmit={handleSubmit}>
        {admin.archived && (
          <p className="text-warning text-sm" role="status">
            This admin is archived. Restore the admin before making changes.
          </p>
        )}
        <div className="grid gap-2.5 sm:grid-cols-2">
          <InputControlled
            defaultValue={admin.email}
            disabled
            label="Email"
            name="email"
            type="email"
          />
          <InputControlled
            disabled={admin.archived}
            label="First name"
            name="firstName"
          />
          <InputControlled
            disabled={admin.archived}
            label="Last name"
            name="lastName"
          />
          <InputControlled
            disabled={admin.archived}
            label="Phone"
            name="phone"
            variant="phone"
          />
          <InputControlled
            disabled={admin.archived}
            label="Role"
            name="role"
            options={adminRoleOptions}
            placeholder="Select a role"
            variant="select"
          />
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          {admin.archived ? (
            <RestoreAdminAlert adminId={id} adminName={adminName} />
          ) : (
            <ArchiveAdminAlert
              adminId={id}
              adminName={adminName}
              isDisabled={isPending}
            />
          )}
          {!admin.archived && (
            <>
              <Anchor
                className="min-w-40 uppercase"
                disabled={isPending}
                rounded="lg"
                size="lg"
                to="/admins"
                variant="destructive"
              >
                Cancel
              </Anchor>
              <Button
                className="min-w-52"
                isLoading={isPending}
                size="lg"
                type="submit"
              >
                Save
              </Button>
            </>
          )}
        </div>
      </form>
    </Form.Provider>
  );
}
